import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/Navbar';
import { RachaPanel } from '../components/RachaPanel';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, RefreshCw, ChevronDown, ChevronUp,
  Shield, Search, Flame, Target, Zap, Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';

const ConfidenceRing = ({ value }) => {
  const color = value >= 85 ? '#34d399' : value >= 70 ? '#fbbf24' : '#f87171';
  const circ = 2 * Math.PI * 36;
  const offset = circ - (value / 100) * circ;
  return (
    <div className="relative w-20 h-20 flex items-center justify-center">
      <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 80 80">
        <circle cx="40" cy="40" r="36" fill="none" stroke="#1e293b" strokeWidth="5" />
        <circle cx="40" cy="40" r="36" fill="none" stroke={color} strokeWidth="5"
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          className="transition-all duration-1000" />
      </svg>
      <span className="text-lg font-black text-white z-10">{value}%</span>
    </div>
  );
};

const PrometeoCard = ({ pred, rank }) => {
  const [open, setOpen] = useState(rank <= 2);

  return (
    <div className={`rounded-xl overflow-hidden border transition-all ${
      rank === 1 ? 'bg-gradient-to-br from-slate-900 via-orange-950/10 to-slate-900 border-orange-600/40 shadow-lg shadow-orange-900/10'
        : 'bg-slate-900/70 border-slate-800/50'
    }`} data-testid={`prometeo-card-${pred.lottery_id}`}>
      <button className="w-full p-4 flex items-center justify-between hover:bg-slate-800/20 transition-all"
        onClick={() => setOpen(!open)} data-testid={`prometeo-toggle-${pred.lottery_id}`}>
        <div className="flex items-center gap-3">
          {rank <= 3 && (
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-black text-sm ${
              rank === 1 ? 'bg-orange-500/20 text-orange-400' : rank === 2 ? 'bg-slate-400/20 text-slate-300' : 'bg-amber-800/20 text-amber-400'
            }`}>#{rank}</div>
          )}
          <div className="text-left">
            <h3 className="text-white font-bold text-sm">{pred.lottery_name}</h3>
            <span className="text-[10px] text-slate-500">{pred.draws_analyzed} sorteos | {pred.draw_time}</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            {pred.numbers.map((n, i) => (
              <div key={`pn-${n}`} className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 via-amber-500 to-yellow-500 text-slate-950 flex items-center justify-center font-black text-sm shadow-lg shadow-orange-500/30"
                style={{ animation: `fadeIn 0.4s ease-out ${i * 0.1}s both` }}>
                {String(n).padStart(2, '0')}
              </div>
            ))}
          </div>
          <Badge variant="outline" className={`font-bold text-xs ${
            pred.confidence >= 85 ? 'text-emerald-400 border-emerald-500/30' : 'text-amber-400 border-amber-500/30'
          }`}>{pred.confidence}%</Badge>
          {open ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-slate-800/50 space-y-4" style={{ animation: 'fadeIn 0.3s ease-out' }}>
          <div className="flex items-start gap-5 mt-3">
            <ConfidenceRing value={pred.confidence} />
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span>Ultimo sorteo:</span>
                <div className="flex gap-1">
                  {pred.last_draw?.map((n, i) => (
                    <span key={`ld-${n}`} className="w-7 h-7 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-[10px] font-bold text-slate-400">
                      {String(n).padStart(2, '0')}
                    </span>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="p-2 bg-slate-800/40 rounded-lg">
                  <span className="text-slate-500">Terminales</span>
                  <p className="text-orange-400 font-bold">{pred.terminal_match}/3 match</p>
                </div>
                <div className="p-2 bg-slate-800/40 rounded-lg">
                  <span className="text-slate-500">Vecinos</span>
                  <p className="text-cyan-400 font-bold">{pred.neighbor_count} encontrados</p>
                </div>
                <div className="p-2 bg-slate-800/40 rounded-lg">
                  <span className="text-slate-500">Pools</span>
                  <p className="text-emerald-400 font-bold">{pred.total_pool_membership || 0}/12</p>
                </div>
              </div>
              {pred.calibrated_weights && (
                <div className="flex gap-1 flex-wrap mt-1">
                  {Object.entries(pred.calibrated_weights).sort(([,a],[,b]) => b - a).map(([k, v]) => (
                    <span key={`w-${k}`} className="px-1.5 py-0.5 bg-slate-800/60 border border-slate-700/30 rounded text-[9px] text-slate-400">
                      {k === 'same_terminal' ? 'Terminal' : k === 'neighbor' ? 'Vecino' : k === 'frequent' ? 'Frecuente' : 'Vencido'}: <span className="text-white font-bold">{Math.round(v * 100)}%</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* STRONG NUMBERS — High probability additional picks */}
          {pred.strong_numbers?.length > 0 && (
            <div className="p-3 bg-gradient-to-r from-emerald-950/30 to-slate-900/30 rounded-xl border border-emerald-800/30">
              <span className="text-emerald-400 text-xs font-bold flex items-center gap-1 mb-2">
                <Zap size={12} /> Numeros Fuertes — Juega estos tambien
              </span>
              <div className="flex gap-2 flex-wrap">
                {pred.strong_numbers.map((s) => (
                  <div key={`str-${s.number}`} className="flex items-center gap-1 px-2 py-1 bg-slate-800/60 border border-emerald-700/30 rounded-lg">
                    <span className="w-7 h-7 rounded-full bg-emerald-600/30 border border-emerald-500/40 flex items-center justify-center text-xs font-black text-emerald-300">
                      {String(s.number).padStart(2, '0')}
                    </span>
                    <div className="flex gap-0.5">
                      {s.in_same_terminal && <span className="w-1.5 h-1.5 rounded-full bg-orange-400" title="Terminal" />}
                      {s.in_neighbor && <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" title="Vecino" />}
                      {s.in_frequent && <span className="w-1.5 h-1.5 rounded-full bg-purple-400" title="Frecuente" />}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-slate-600 text-[9px] mt-1.5">
                Estos numeros aparecen en varios grupos de analisis. Consulta el historial antes de decidir si deseas jugar.
              </p>
            </div>
          )}

          {/* Breakdown */}
          <div className="space-y-2">
            <h4 className="text-xs text-slate-400 font-bold flex items-center gap-1"><Eye size={12} /> Analisis por Numero</h4>
            {pred.breakdown?.map((b) => (
              <div key={`bd-${b.number}`} className="p-3 bg-slate-800/30 rounded-lg border border-slate-700/20">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 text-slate-950 flex items-center justify-center font-black text-xs">
                      {String(b.number).padStart(2, '0')}
                    </div>
                    <div>
                      <span className="text-white font-bold text-xs">Posicion {b.position}</span>
                      <span className="text-slate-500 text-[10px] ml-2">T:{b.terminal} D:{b.decade}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {b.pools >= 2 && (
                      <Badge variant="outline" className="text-[9px] py-0 border-emerald-500/40 text-emerald-300">
                        {b.pools} pools
                      </Badge>
                    )}
                    {b.in_same_terminal && <Badge variant="outline" className="text-[9px] py-0 border-orange-500/40 text-orange-300">Terminal</Badge>}
                    {b.in_neighbor && <Badge variant="outline" className="text-[9px] py-0 border-cyan-500/40 text-cyan-300">Vecino</Badge>}
                    {b.in_frequent && <Badge variant="outline" className="text-[9px] py-0 border-purple-500/40 text-purple-300">Frecuente</Badge>}
                    {b.is_overdue && <Badge variant="outline" className="text-[9px] py-0 border-red-500/40 text-red-300">Vencido</Badge>}
                    {b.streak >= 3 && <Badge variant="outline" className="text-[9px] py-0 border-amber-500/40 text-amber-300">Racha x{b.streak}</Badge>}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-1 text-[9px]">
                  {Object.entries(b.details || {}).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-1">
                      <div className="h-1 flex-1 bg-slate-800 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${v >= 60 ? 'bg-emerald-400' : v >= 30 ? 'bg-amber-400' : 'bg-slate-600'}`}
                          style={{ width: `${Math.min(v, 100)}%` }} />
                      </div>
                      <span className="text-slate-500 w-16 truncate">{k}</span>
                      <span className="text-white font-bold w-6 text-right">{Math.round(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Hot Terminals */}
          <div className="flex items-center gap-4">
            <div>
              <span className="text-slate-500 text-[10px] flex items-center gap-1 mb-1"><Flame size={10} className="text-orange-400" /> Terminales calientes</span>
              <div className="flex gap-1.5">
                {pred.hot_terminals?.map((t) => (
                  <span key={`ht-${t}`} className="w-7 h-7 rounded-lg bg-orange-950/50 border border-orange-700/40 flex items-center justify-center text-xs font-black text-orange-300">{t}</span>
                ))}
              </div>
            </div>
            <div>
              <span className="text-slate-500 text-[10px] block mb-1">Pales</span>
              <div className="flex gap-1">
                {pred.pales?.map((p, i) => (
                  <span key={`pl-${p[0]}-${p[1]}`} className="px-1.5 py-0.5 bg-cyan-950/40 border border-cyan-800/30 rounded text-[10px] text-cyan-300 font-bold">{p[0]}-{p[1]}</span>
                ))}
              </div>
            </div>
            {pred.tripleta?.length === 3 && (
              <div>
                <span className="text-slate-500 text-[10px] block mb-1">Tripleta</span>
                <span className="px-1.5 py-0.5 bg-purple-950/40 border border-purple-800/30 rounded text-[10px] text-purple-300 font-bold">{pred.tripleta.join('-')}</span>
              </div>
            )}
          </div>

          {/* Extended candidates */}
          {pred.extended_candidates?.length > 0 && (
            <div>
              <span className="text-slate-500 text-[10px] flex items-center gap-1 mb-1"><Target size={10} className="text-emerald-400" /> Alternativas por posicion</span>
              <div className="grid grid-cols-3 gap-2">
                {pred.extended_candidates.map((cands, pi) => (
                  <div key={`ext-${pi}`} className="p-1.5 bg-slate-800/20 rounded border border-slate-700/20">
                    <span className="text-[8px] text-slate-600">{['1ra', '2da', '3ra'][pi]}</span>
                    <div className="flex gap-1 flex-wrap mt-0.5">
                      {cands.map(([num, score], j) => (
                        <span key={`ec-${num}`} className={`px-1 py-0.5 rounded text-[9px] font-bold ${
                          j === 0 ? 'bg-emerald-900/40 border border-emerald-700/30 text-emerald-300' : 'bg-slate-800/60 text-slate-500'
                        }`}>{String(num).padStart(2, '0')}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function PrometeoPage() {
  const { user, token } = useAuth();
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/prometeo/today`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 60000,
      });
      setPredictions(res.data.predictions || []);
    } catch (e) {
      toast.error('Error cargando Prometeo');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { if (token) fetchData(); }, [token, fetchData]);

  const filtered = search
    ? predictions.filter(p => p.lottery_name.toLowerCase().includes(search.toLowerCase()))
    : predictions;

  const topConf = predictions.length > 0 ? Math.max(...predictions.map(p => p.confidence)) : 0;
  const avgConf = predictions.length > 0 ? Math.round(predictions.reduce((a, p) => a + p.confidence, 0) / predictions.length) : 0;

  if (!user) return (
    <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center">
      <p className="text-slate-400">Inicia sesion para acceder a Prometeo</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0e1a]" data-testid="prometeo-page">
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-6 pt-20">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white"><ArrowLeft size={16} /></Button>
            </Link>
            <div>
              <h1 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400 flex items-center gap-2">
                <Zap className="text-orange-400" size={24} /> PROMETEO V4
              </h1>
              <p className="text-slate-500 text-sm">Analista Matematico — 3 Pools de Probabilidad Combinada</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}
            className="border-orange-700/40 text-orange-400 hover:text-white" data-testid="prometeo-refresh">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>

        <RachaPanel engine="prometeo" token={token} />

        {!loading && predictions.length > 0 && (
          <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-orange-950/30 via-slate-900/50 to-amber-950/30 border border-orange-900/30"
            style={{ animation: 'fadeIn 0.4s ease-out' }}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
              <div><div className="text-orange-400 text-xl font-black">{predictions.length}</div><div className="text-slate-500 text-xs">Loterias</div></div>
              <div><div className="text-emerald-400 text-xl font-black">{topConf}%</div><div className="text-slate-500 text-xs">Puntaje maximo</div></div>
              <div><div className="text-cyan-400 text-xl font-black">{avgConf}%</div><div className="text-slate-500 text-xs">Promedio</div></div>
              <div><div className="text-amber-400 text-xl font-black">3</div><div className="text-slate-500 text-xs">Pools Combinados</div></div>
            </div>
          </div>
        )}

        <div className="mb-4 p-3 rounded-lg bg-orange-950/20 border border-orange-800/30">
          <div className="flex items-start gap-2 text-xs text-slate-400">
            <Shield size={14} className="text-orange-400 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p>
                <strong className="text-orange-300">Prometeo V4</strong> usa 4 senales validadas por backtest:
                <strong className="text-white"> frecuencia recency-weighted (45%)</strong>,
                <strong className="text-orange-300"> terminales calientes (30%)</strong>,
                <strong className="text-white"> momentum reciente (15%)</strong> y patron del dia (10%).
              </p>
              <p className="text-slate-500 text-[11px]">
                Los puntajes son criterios internos del modelo y no indican probabilidades de ganar. Consulta los resultados historicos antes de jugar.
              </p>
            </div>
          </div>
        </div>

        <div className="mb-4 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input placeholder="Buscar loteria..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="bg-slate-900/70 border-slate-800 text-white pl-10" data-testid="prometeo-search" />
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="animate-spin text-orange-400" size={36} />
            <p className="text-slate-500 text-sm">Prometeo analizando 4 capas validadas...</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="prometeo-list">
            {filtered.map((pred, i) => (
              <PrometeoCard key={pred.lottery_id} pred={pred} rank={i + 1} />
            ))}
            {filtered.length === 0 && (
              <div className="text-center py-12 text-slate-500">No se encontraron loterias</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
